/*
 * Cinesteel cutout: removes plain white photo backgrounds in the browser.
 * For every product image on the page, if its corners are solid white, the white
 * area connected to the edges is made transparent (soft edges), so the product
 * sits straight on the site background. Already-transparent images are skipped.
 * Theme settings > Product photos > "Remove white photo backgrounds".
 */
(function () {
  var SELECTOR = [
    '.featured-collection__image img',
    '.cs-arch__media img',
    '.cs-sleep__save img',
    '.cs-hero--fit-contain .cs-hero__media img'
  ].join(',');
  var HOVER_SELECTOR = '.product-item-hover';
  var MIN_WHITE = 232;  // corners must be at least this bright to count as a white(ish) background
  var TOLERANCE = 4;    // how far a pixel may be from the background color and still be removed
  var FEATHER = 14;     // edge pixels within this distance fade out instead of staying hard
  var MAX_SIDE = 1000;  // work at most at this resolution
  var MIN_COVERAGE = 0.6; // below this the cut ate part of the product, so the original is kept
  var bg = [255, 255, 255];

  function dist(d, i) {
    return Math.max(Math.abs(d[i] - bg[0]), Math.abs(d[i + 1] - bg[1]), Math.abs(d[i + 2] - bg[2]));
  }

  function isWhite(d, i) {
    return d[i + 3] > 200 && dist(d, i) <= TOLERANCE;
  }

  function isWhiteCorner(d, i) {
    return d[i + 3] > 200 && d[i] >= MIN_WHITE && d[i + 1] >= MIN_WHITE && d[i + 2] >= MIN_WHITE;
  }

  function coverage(d, w, h) {
    var rows = new Uint32Array(h), cols = new Uint32Array(w), k, x, y;
    for (k = 0; k < w * h; k++) if (d[k * 4 + 3] >= 128) { rows[(k / w) | 0]++; cols[k % w]++; }
    var rMin = Math.max(1, w * 0.01), cMin = Math.max(1, h * 0.01);
    var y0 = 0, y1 = h - 1, x0 = 0, x1 = w - 1;
    while (y0 < h && rows[y0] < rMin) y0++;
    while (y1 > y0 && rows[y1] < rMin) y1--;
    while (x0 < w && cols[x0] < cMin) x0++;
    while (x1 > x0 && cols[x1] < cMin) x1--;
    if (y0 >= h || x0 >= w) return 0;
    var solid = 0;
    for (y = y0; y <= y1; y++) for (x = x0; x <= x1; x++) if (d[(y * w + x) * 4 + 3] >= 128) solid++;
    return solid / ((x1 - x0 + 1) * (y1 - y0 + 1));
  }

  // Returns a blob URL of the cut-out image, or null when the image doesn't have a white background.
  function cutout(source, done) {
    var w = source.naturalWidth || source.width;
    var h = source.naturalHeight || source.height;
    if (!w || !h) return done(null);
    var scale = Math.min(1, MAX_SIDE / Math.max(w, h));
    w = Math.round(w * scale);
    h = Math.round(h * scale);

    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    try {
      ctx.drawImage(source, 0, 0, w, h);
      var img = ctx.getImageData(0, 0, w, h);
    } catch (e) {
      return done(null); // cross-origin image we can't read
    }
    var d = img.data;

    // Only act when at least 3 corners are solid white
    var corners = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + w - 1) * 4];
    var whiteCorners = corners.filter(function (i) { return isWhiteCorner(d, i); });
    if (whiteCorners.length < 3) return done(null);
    bg = [0, 1, 2].map(function (c) {
      return Math.round(whiteCorners.reduce(function (sum, i) { return sum + d[i + c]; }, 0) / whiteCorners.length);
    });

    // Flood fill from every white border pixel
    var seen = new Uint8Array(w * h);
    var stack = [];
    function push(p) {
      if (!seen[p] && isWhite(d, p * 4)) { seen[p] = 1; stack.push(p); }
    }
    for (var x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
    for (var y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }
    while (stack.length) {
      var p = stack.pop();
      var px = p % w;
      if (px > 0) push(p - 1);
      if (px < w - 1) push(p + 1);
      if (p >= w) push(p - w);
      if (p < (h - 1) * w) push(p + w);
    }

    // Clear the background, then soften the pixels along its edge
    for (var q = 0; q < seen.length; q++) if (seen[q]) d[q * 4 + 3] = 0;
    for (var r = 0; r < seen.length; r++) {
      if (seen[r]) continue;
      var rx = r % w;
      var touches = (rx > 0 && seen[r - 1]) || (rx < w - 1 && seen[r + 1]) || (r >= w && seen[r - w]) || (r < (h - 1) * w && seen[r + w]);
      if (!touches) continue;
      var i4 = r * 4;
      var gap = dist(d, i4);
      if (gap < TOLERANCE + FEATHER) d[i4 + 3] = Math.round(d[i4 + 3] * (gap - TOLERANCE) / FEATHER);
    }

    // Safety check: a garment fills most of its own bounding box (~0.7). If the cut left far
    // less (a white garment melting into a white background), keep the original photo.
    // The box ignores stray specks: only rows/columns with real content count.
    if (coverage(d, w, h) < MIN_COVERAGE) return done(null);

    ctx.putImageData(img, 0, 0);
    canvas.toBlob(function (blob) { done(blob ? URL.createObjectURL(blob) : null); }, 'image/png');
  }

  // Lazy-loaded images swap in their real photo late; wait for that before cutting.
  function stillLoading(el) {
    return el.classList.contains('lazyload') || el.classList.contains('lazyloading');
  }

  function applyCut(el) {
    el.removeAttribute('srcset');
    el.removeAttribute('data-srcset');
    el.removeAttribute('sizes');
    if (el.src !== el._csCutUrl) el.src = el._csCutUrl;
  }

  function processImg(el) {
    var src = el.currentSrc || el.src;
    if (!src || src.indexOf('data:') === 0) return;
    // Already cut, but the theme swapped the original back in (late srcset): re-apply.
    if (el.dataset.csCut === 'done') { if (src !== el._csCutUrl) applyCut(el); return; }
    if (el.dataset.csCut || stillLoading(el) || src.indexOf('blob:') === 0) return;
    el.dataset.csCut = 'working';
    cutout(el, function (url) {
      if (!url) { el.dataset.csCut = 'skip'; return; }
      el._csCutUrl = url;
      el.dataset.csCut = 'done';
      applyCut(el);
    });
  }

  function processHover(el) {
    if (el.dataset.csCut) return;
    var bg = getComputedStyle(el).backgroundImage;
    var match = bg && bg.match(/url\(["']?([^"')]+)["']?\)/);
    if (!match) return;
    el.dataset.csCut = 'working';
    var probe = new Image();
    probe.onload = function () {
      cutout(probe, function (url) {
        if (!url) { el.dataset.csCut = 'skip'; return; }
        el.dataset.csCut = 'done';
        el.style.backgroundImage = 'url("' + url + '")';
        el.style.backgroundColor = 'transparent';
      });
    };
    probe.onerror = function () { el.dataset.csCut = 'skip'; };
    probe.src = match[1];
  }

  function watch(el) {
    if (el.dataset.csWatch) return;
    el.dataset.csWatch = '1';
    if (el.complete && el.naturalWidth) processImg(el);
    el.addEventListener('load', function () { processImg(el); });
    el.addEventListener('lazyloaded', function () { processImg(el); });
  }

  function scan() {
    document.querySelectorAll(SELECTOR).forEach(function (el) {
      watch(el);
      if (el.complete && el.naturalWidth) processImg(el);
    });
    document.querySelectorAll(HOVER_SELECTOR).forEach(function (el) {
      if (!el.dataset.csCut && getComputedStyle(el).backgroundImage !== 'none') processHover(el);
    });
  }

  var queued = null;
  function scanSoon() {
    if (queued) return;
    queued = setTimeout(function () { queued = null; scan(); }, 250);
  }

  function init() {
    scan();
    document.addEventListener('lazyloaded', scanSoon);
    new MutationObserver(scanSoon).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-bgset'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
